import React, { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import useFetchApplications from "../hooks/useFetchApplications";
import useDownloadAssignmentsStatusReport from "../hooks/useDownloadAssignmentsStatusReport";

const normalizeApplications = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.applications)) return data.applications;
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

const formatApplicationLabel = (application) => {
  const name = application?.name?.trim() || "Aplicación sin nombre";
  return `${name} (ID: ${application.id})`;
};

const ReportsPage = () => {
  const { data: applicationsData, loading: loadingApplications, error: applicationsError } = useFetchApplications();
  const { downloadAssignmentsStatusReport, loading: downloadingReport, error: downloadReportError } = useDownloadAssignmentsStatusReport();

  const [selectedApplications, setSelectedApplications] = useState([]);

  const applications = useMemo(() => {
    return normalizeApplications(applicationsData)
      .filter((application) => application?.id != null)
      .sort((a, b) => Number(b.id) - Number(a.id));
  }, [applicationsData]);

  const selectedApplicationIds = useMemo(() => selectedApplications.map((application) => application.id), [selectedApplications]);

  const allApplicationsSelected = applications.length > 0 && selectedApplications.length === applications.length;

  useEffect(() => {
    const availableApplicationsById = new Map(applications.map((application) => [application.id, application]));

    setSelectedApplications((currentSelection) =>
      currentSelection
        .filter((application) => availableApplicationsById.has(application.id))
        .map((application) => availableApplicationsById.get(application.id)),
    );
  }, [applications]);

  const handleToggleAllApplications = () => {
    setSelectedApplications(allApplicationsSelected ? [] : applications);
  };

  const handleClearSelection = () => {
    setSelectedApplications([]);
  };

  const handleDownload = async () => {
    try {
      await downloadAssignmentsStatusReport(selectedApplicationIds);
    } catch (error) {
      // El error ya queda almacenado y se muestra desde el hook.
    }
  };

  return (
    <Box sx={{ minWidth: "100%", p: { xs: 2, md: 5 }, overflowX: "auto" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: { xs: 2, md: 4 },
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#FFF8C5",
                color: "#5F5F00",
                flexShrink: 0,
              }}
            >
              <AssessmentOutlinedIcon />
            </Box>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#333333" }}>
                Reporte por aplicaciones
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Seleccione una o varias aplicaciones para generar un único archivo consolidado.
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Aplicaciones que incluirá el reporte
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
            Puede buscar por nombre o por el ID de la aplicación.
          </Typography>

          {applicationsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {applicationsError.message || "No fue posible cargar las aplicaciones."}
            </Alert>
          )}

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={applications}
            value={selectedApplications}
            loading={loadingApplications}
            onChange={(event, newValue) => setSelectedApplications(newValue)}
            getOptionLabel={formatApplicationLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No se encontraron aplicaciones"
            loadingText="Cargando aplicaciones..."
            filterOptions={(options, state) => {
              const search = state.inputValue.trim().toLowerCase();

              if (!search) return options;

              return options.filter((application) => {
                const name = String(application.name || "").toLowerCase();
                const id = String(application.id || "").toLowerCase();

                return name.includes(search) || id.includes(search);
              });
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;

              return (
                <li key={option.id ?? key} {...optionProps}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name || "Aplicación sin nombre"}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id}
                      {option.date_min || option.date_max
                        ? ` · ${option.date_min || "Sin fecha inicial"} - ${option.date_max || "Sin fecha final"}`
                        : ""}
                    </Typography>
                  </Box>
                </li>
              );
            }}
            renderTags={(value, getTagProps) => (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 150,
                  overflowY: "auto",
                }}
              >
                {value.map((application, index) => {
                  const { key, ...tagProps } = getTagProps({ index });

                  return (
                    <Chip
                      key={application.id ?? key}
                      label={`${application.name || "Aplicación"} · ID ${application.id}`}
                      {...tagProps}
                      sx={{ maxWidth: "100%" }}
                    />
                  );
                })}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar y seleccionar aplicaciones"
                placeholder={selectedApplications.length === 0 ? "Escriba para buscar..." : ""}
                helperText={
                  selectedApplications.length === 0
                    ? "Debe seleccionar al menos una aplicación."
                    : `${selectedApplications.length} ${
                        selectedApplications.length === 1 ? "aplicación seleccionada" : "aplicaciones seleccionadas"
                      }.`
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingApplications ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={handleToggleAllApplications}
              disabled={loadingApplications || applications.length === 0}
              sx={{ textTransform: "none" }}
            >
              {allApplicationsSelected ? "Deseleccionar todas" : "Seleccionar todas"}
            </Button>

            <Button
              variant="text"
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={handleClearSelection}
              disabled={selectedApplications.length === 0}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Limpiar selección
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#FAFAFA",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Resumen de la selección
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  El reporte consolidará la información de {selectedApplications.length}{" "}
                  {selectedApplications.length === 1 ? "aplicación" : "aplicaciones"}.
                </Typography>
              </Box>

              <Button
                variant="contained"
                startIcon={downloadingReport ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={selectedApplications.length === 0 || loadingApplications || downloadingReport}
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {downloadingReport ? "Generando reporte..." : "Descargar informe de estados"}
              </Button>
            </Stack>
          </Box>

          {/* Error generado al descargar el reporte */}
          {downloadReportError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {downloadReportError.message || "No fue posible descargar el informe de estados."}
            </Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ReportsPage;
